import { AssessmentStatus as Assessment_Status_Enum } from '@risksmart-app/domain/src/types/consts/assessment-status';
import type { UpdateAssessmentMutation } from '@risksmart-app/web-graphql-client/generated/graphql';
import {
  namedOperations,
  UpdateAssessmentDocument,
} from '@risksmart-app/web-graphql-client/generated/graphql';

import { createMutationHook } from '@/utils/hook-factories';

import type { UpdateAssessmentInput, UpdateAssessmentRequest } from './types';

export const useUpdateAssessment = createMutationHook({
  name: 'updateAssessment',
  mutationOptions: (trpc, onSuccess) =>
    trpc.frontend.assessment.update.mutationOptions({ onSuccess }),
  mapInput: ({
    ...variables
  }: UpdateAssessmentInput): UpdateAssessmentRequest => ({
    ...variables,
    Title: variables.Title ?? '',
    Status: variables.Status ?? Assessment_Status_Enum.NotStarted,
  }),
  invalidate: (trpc) => [
    // COI-889: router-level key — every assessment-result read embeds its
    // parent assessment's Title and Status, which this hook writes.
    trpc.frontend.assessment.pathKey(),
    // COI-471 parity: Apollo twin evicts the assessment-result fields
    // (result ratings roll up into the assessment).
    trpc.frontend.documentAssessmentResult.pathKey(),
    trpc.frontend.obligationAssessmentResult.pathKey(),
    trpc.frontend.riskAssessmentResult.pathKey(),
    // COI-889: the my-items ribbon counts this entity.
    trpc.frontend.myItems.pathKey(),
    // COI-889: this hook rewrites the owner/contributor sets, whose triggers
    // maintain the grant cache `perm_v2_entity_permissions` reads (60-min
    // window, `refetchOnMount: false`, so it never self-heals).
    trpc.frontend.permission.getEntityPermissions.queryKey(),
  ],
  mapResponse: (): UpdateAssessmentMutation => ({
    updateAssessmentApi: {
      __typename: 'GenericMutationOutput',
      affected_rows: 1,
    },
  }),
  graphqlDocument: UpdateAssessmentDocument,
  graphqlVariables: ({
    OriginalTimestamp: _token,
    ...variables
  }: UpdateAssessmentInput) => ({
    object: variables,
  }),
  evictFields: [
    'risk_assessment_result',
    'document_assessment_result',
    'obligation_assessment_result',
    'assessment',
  ],
  refetchQueries: [
    namedOperations.Query.getAssessmentById,
    namedOperations.Query.getAssessments,
  ],
});
